import { roundMajority, suggestPoints } from '../../utils/roundSuggestion';
import { useEffect, useReducer, useRef, useState, useCallback } from 'react';
import { refusalOf } from '../../lib/httpError';
import { fillTemplate } from '../../lib/fillTemplate';
import type {
  Bout, BoutVerdict, JudgeCardRound, JudgeCardRow, JuryOverrideAction, JuryPair, OutcomeCode, PublicRoundScore, RoundScore,
  SocketBoutDisplayRoundsUpdated,
} from '../../types';
import { getBoutScores, submitVerdict, updateDisplayRounds } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { useI18n } from '../../i18n';
import { winnerLabel } from '../../i18n/labels';
import { useSocket, useRooms, bindSocket } from '../../hooks/useSocket';
import { useToast } from '../../hooks/useToast';
import { useBusy } from '../../hooks/useBusy';
import { useConfirm } from '../../hooks/useConfirm';
import { useDeferredLoading } from '../../hooks/useDeferredLoading';
import { logBackgroundError } from '../../observability';
import { personName } from '../../utils/personName';
import { boutRounds } from '../../utils/tournament';
import { boutSeatsOf } from '../../utils/boutSeats';
import JuryScoringTable, { type JuryRoundState } from './JuryScoringTable';
import PublicScoreboard from './PublicScoreboard';
import VerdictForm from './VerdictForm';
import { useOnResume } from '../../lib/resume';

export interface VerdictFormState {
  winner: '' | 'red' | 'blue' | 'draw';
  outcome: string;
  round_ended: string;
  red_corner_score: string;
  blue_corner_score: string;
}

interface BoutCardProps {
  bout: Bout;
  onVerdictSubmitted?: () => void;
  /** True if the current user was assigned to this bout (side judge or ring ref) */
  isAssignedToBout?: boolean;
}

export default function BoutCard({ bout, onVerdictSubmitted, isAssignedToBout = false }: BoutCardProps) {
  const { hasRole, user } = useAuthStore();
  const { t } = useI18n();
  const socket = useSocket();
  const { showError } = useToast();
  const { confirm } = useConfirm();

  // isActualJury: the jury assigned to THIS bout -- can approve rounds + submit verdict
  // canOversee: the jury or an admin -- sees the detailed scoring table (an admin reads only)
  const isActualJury = boutSeatsOf(bout, user?.id).jury;
  const canOversee = isActualJury || (!!user?.id && hasRole('admin'));
  // After scoring, assigned referees (side judges + ring ref) can also see detailed scores
  const canViewDetailedScores = canOversee || (isAssignedToBout && bout.status === 'scored');
  const hasVerdict = !!bout.verdict || bout.status === 'scored';
  const canToggleDisplay = isActualJury; // only real jury, not admin
  const canSubmitVerdict = isActualJury; // only real jury, not admin

  // #664 (F49) — the rooms of this card. This component holds the ONLY
  // score:submitted listener in the app. That event goes to
  // bout:<id>:officials, so the card joins that room itself. It used to rely
  // on BoutDetail. That worked only because a listener belongs to the shared
  // socket, not to a room. The room is for the cards that show the detailed
  // scores. Joining only then keeps the DB check of the server off the public
  // path. `useSocket` counts the references, so this join and the join of
  // BoutDetail exist together, and neither unmount removes the other.
  useRooms([`bout:${bout.id}`, canViewDetailedScores && `bout:${bout.id}:officials`]);

  const [scores, setScores] = useState<JudgeCardRow[]>([]);
  const [publicScores, setPublicScores] = useState<PublicRoundScore[]>([]);
  // The BACKEND decides which view this caller gets (#453). The local guess,
  // canViewDetailedScores, still picks the socket room; the rows are read by
  // the `view` the response names, so a wrong guess cannot misread them.
  const [officialsView, setOfficialsView] = useState(canViewDetailedScores);
  const [verdict, setVerdict] = useState<BoutVerdict | null>(bout.verdict ?? null);
  const [displayRounds, setDisplayRounds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  // #458 — the loading text shows after 200 ms and holds 400 ms (useDeferredLoading):
  // a fast load shows nothing, not a one-frame flash.
  const showLoading = useDeferredLoading(loading);
  const [savingRound, setSavingRound] = useState<number | null>(null);
  /** #605 — the lock of a toggle of an approved round: one toggle at a time. */
  const toggle = useBusy();
  /** #605 — the count of the toggles that started, and of the toggles that got
   *  an answer. A load of the scores that started before the last answer can
   *  hold the rounds from before the write. It must not replace the rounds on
   *  screen: the next tick then sent a list without that round, and the server
   *  removed the approval. */
  const toggles = useRef({ started: 0, answered: 0 });
  // A tab change keeps the path, so the provider does not cancel a late question: the card checks its own mount.
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  /** #605 — the lock of Submit Verdict: a second tap sends no second POST. */
  const verdictSend = useBusy();

  /** The save state of the jury totals (JuryScoringTable, #599). */
  const juryRounds = useRef<JuryRoundState>({ unsaved: new Set(), settled: new Map(), clock: 0 });
  const [overrides, setOverride] = useReducer(
    (state: Record<number, JuryPair>, action: JuryOverrideAction) => ({
      ...state,
      [action.round]: { ...(state[action.round] || { red: null, blue: null }), [action.key]: action.value },
    }),
    { 1: { red: null, blue: null }, 2: { red: null, blue: null }, 3: { red: null, blue: null } }
  );

  // Verdict form state holds ONLY user overrides. Empty strings mean "use
  // the auto-default computed from totals + approved rounds" (merged at
  // render time into `displayedVerdictForm` below). This replaces the
  // earlier derived-state useEffect — see `displayedVerdictForm` block.
  const [verdictForm, setVerdictForm] = useState<VerdictFormState>({
    winner: '', outcome: '', round_ended: '',
    red_corner_score: '', blue_corner_score: '',
  });

  // #602 — 3 rounds, or 5 for a bout of a class whose max_rounds is 5.
  const rounds = Array.from({ length: boutRounds(bout) }, (_, i) => i + 1);

  // -- Load scores --
  const loadScores = useCallback(async () => {
    try {
      const startedAt = juryRounds.current.clock;
      const answeredAt = toggles.current.answered;
      const scoresRes = await getBoutScores(bout.id);

      const serverVerdict = scoresRes.data.verdict;
      if (serverVerdict) setVerdict(serverVerdict);

      // #605 — a toggle runs now, or a toggle answered after the start of this
      // load. Then this answer can be older than the write: the screen keeps
      // its approved rounds and its jury totals.
      const olderThanToggle = toggles.current.started !== answeredAt;
      if (!olderThanToggle) {
        const serverDisplayRounds: number[] = scoresRes.data.display_rounds ?? [];
        setDisplayRounds(serverDisplayRounds);

        // Load server-persisted overrides. A round whose total the jury changed
        // keeps the total on screen until the server holds it, and a save that
        // succeeded after the start of this load wins over its answer (#599).
        const serverOverrides: Record<string, { red: number; blue: number }> = scoresRes.data.round_overrides || {};
        for (const [rnd, ov] of Object.entries(serverOverrides)) {
          const r = Number(rnd);
          if (juryRounds.current.unsaved.has(r) || (juryRounds.current.settled.get(r) ?? 0) > startedAt) continue;
          setOverride({ round: Number(rnd), key: 'red', value: ov.red });
          setOverride({ round: Number(rnd), key: 'blue', value: ov.blue });
        }
      }

      // A response with no `view` comes from a backend older than #453, which
      // a rolling release can still serve for a moment: keep the local guess.
      const officials = scoresRes.data.view
        ? scoresRes.data.view === 'officials'
        : canViewDetailedScores;
      setOfficialsView(officials);
      if (officials) {
        // Group individual judge scores by referee_id for the detailed jury table
        const grouped: Record<string, JudgeCardRow> = {};
        for (const s of (scoresRes.data.scores || []) as RoundScore[]) {
          if (!grouped[s.referee_id]) {
            grouped[s.referee_id] = {
              referee_id: s.referee_id,
              referee_name: personName(s.first_name, s.last_name),
              rounds: {},
            };
          }
          grouped[s.referee_id].rounds[s.round_number] = {
            red_points: s.red_points, blue_points: s.blue_points,
            red_warnings: s.red_warnings, blue_warnings: s.blue_warnings,
            red_knockdowns: s.red_knockdowns, blue_knockdowns: s.blue_knockdowns,
            red_cautions: s.red_cautions, blue_cautions: s.blue_cautions,
            round_outcome: s.round_outcome ?? null,
          };
        }
        setScores(Object.values(grouped));
      } else {
        // Public path: backend returns one total per approved round
        setPublicScores((scoresRes.data.scores ?? []) as PublicRoundScore[]);
      }

    } finally {
      setLoading(false);
    }
    // bout.status: reload when the bout is scored, because its side judges
    // and ring referee then get the officials view (#453).
  }, [bout.id, bout.status, canViewDetailedScores]);

  useEffect(() => { loadScores(); }, [loadScores]);
  // #499 — reload the scores after a gap in the socket.
  useOnResume(loadScores, 'bout-card');

  // -- Live socket updates --
  //
  // Note: `bindSocket()` removes the listeners with the SAME references that it
  // added. An `off(event)` with no handler removes ALL listeners of that event
  // on the socket, and this silently removes the listeners of the other
  // components. This component had that bug before the Apr 2026 hygiene audit.
  useEffect(() => {
    if (!socket) return;

    function onDisplayUpdate({ boutId, display_rounds: dr }: SocketBoutDisplayRoundsUpdated) {
      if (boutId !== bout.id) return;
      setDisplayRounds(dr);
      if (!officialsView) {
        loadScores().catch(logBackgroundError('BoutCard:public-scores'));
      }
    }

    function onScoreUpdate() {
      if (officialsView) loadScores().catch(logBackgroundError('BoutCard:scores'));
    }

    function onPublicScores() {
      if (!officialsView) loadScores().catch(logBackgroundError('BoutCard:public-scores'));
    }

    return bindSocket(socket, {
      'bout:display_rounds_updated': onDisplayUpdate,
      'score:submitted': onScoreUpdate,
      'bout:public_scores_updated': onPublicScores,
    });
  }, [socket, bout.id, officialsView, loadScores]);

  // -- Toggle approved round --
  async function handleDisplayToggle(round: number, checked: boolean) {
    if (!canToggleDisplay || hasVerdict) return;
    // #605 — one toggle at a time. A second event in the same task sends no copy.
    await toggle.run(async () => {
      const newRounds = checked
        ? [...new Set([...displayRounds, round])].sort()
        : displayRounds.filter(r => r !== round);

      // Capture pre-update value for rollback — `displayRounds` in the closure
      // is the pre-update state (setDisplayRounds doesn't mutate it), so this
      // is literally `const prevRounds = displayRounds;` but written
      // defensively in case the code moves later.
      const prevRounds = displayRounds;
      setSavingRound(round);
      setDisplayRounds(newRounds);
      toggles.current.started++;
      try {
        await updateDisplayRounds(bout.id, newRounds);
      } catch (err) {
        // #453 — fewer than 3 judges scored the round: the backend counts the
        // cards and names them, and the jury decides (operator, 2026-09-24).
        // #665 — this question is inside run() on purpose: only the refusal
        // ROUND_FEW_CARDS of the server starts it (#599), and the lock keeps a
        // second toggle out until the jury answers.
        const ask = fewCardsPrompt(err);
        if (!mounted.current) { setDisplayRounds(prevRounds); return; }
        if (ask && await confirm({ message: ask, confirmLabel: t.bout.approveRound })) {
          await updateDisplayRounds(bout.id, newRounds, true)
            .catch((retryErr: unknown) => { setDisplayRounds(prevRounds); showError(retryErr); });
        } else {
          setDisplayRounds(prevRounds);
          if (!ask) showError(err);   // the revert alone told the user nothing (2026-09-19)
        }
      } finally {
        // #605 — a load of the scores that started before this answer keeps the rounds on screen.
        toggles.current.answered++;
        setSavingRound(null);
      }
    });
  }

  /** The confirm text for a 409 ROUND_FEW_CARDS, or null for any other error. */
  function fewCardsPrompt(err: unknown): string | null {
    const refusal = refusalOf(err);
    if (refusal?.code !== 'ROUND_FEW_CARDS') return null;
    return fillTemplate(t.bout.approveFewCards, refusal.params, t);
  }

  // Bout totals sum APPROVED rounds only. An unapproved round is still a
  // draft — side judges may keep scoring, the jury may still override the
  // per-round total — so it isn't part of the official bout total yet. This
  // keeps the verdict form's Red/Blue totals, Winner, and Round Ended in
  // sync as the jury approves rounds one-by-one (IFMA v3.057 §29.2, §30).
  function calcTotalsFromRows(scoreRows: JudgeCardRow[]) {
    let red = 0, blue = 0;
    for (const r of displayRounds) {
      const ov = overrides[r];
      // A jury total is a pair: a half pair is no total, as in verdict.ts.
      if (ov && ov.red != null && ov.blue != null) { red += ov.red; blue += ov.blue; continue; }
      // #599 — the suggestion of the cards (utils/roundSuggestion.ts, the rule of the backend).
      const cards = scoreRows.map(row => row.rounds[r]).filter((c): c is JudgeCardRound => !!c);
      const sRed = suggestPoints(cards.map(c => c.red_points));
      const sBlue = suggestPoints(cards.map(c => c.blue_points));
      if (sRed != null && sBlue != null) { red += sRed; blue += sBlue; }
    }
    return { red, blue };
  }

  const totals = calcTotalsFromRows(scores);

  // #469 — on equal final scores the jury picks: IFMA v3.057 §29.3 decides
  // by the whole bout, not by the rounds won. As information only, the
  // approved rounds that more than half of the judges gave to each athlete,
  // as fought (the round outcome of each card; an old card falls back to its
  // points). #599: the majority of utils/roundSuggestion.ts; for 3 cards it
  // is the count of before.
  const roundsWon = { red: 0, blue: 0 };
  for (const r of displayRounds) {
    const outcomes = scores.map(row => row.rounds[r]).filter((c): c is JudgeCardRound => !!c)
      .map(c => c.round_outcome ?? (c.red_points > c.blue_points ? 'red' : c.blue_points > c.red_points ? 'blue' : 'even'));
    const side = roundMajority(outcomes).side;
    if (side === 'red') roundsWon.red++;
    else if (side === 'blue') roundsWon.blue++;
  }

  // -- Auto-computed verdict-form defaults (render-time, no useEffect) --
  // Previously this was a derived-state useEffect that setState'd into
  // verdictForm and caused a double-render. Now computed per render from
  // totals + displayRounds; user overrides (stored in verdictForm state)
  // take precedence via the merged view below. See the plan
  // f75d2911:RPI/plan/_archive/2026-04/frontend-component-hygiene-audit.md § bucket-5.
  const allRoundsApproved = rounds.every(r => displayRounds.includes(r));
  const highestApproved = displayRounds.length > 0 ? Math.max(...displayRounds) : 0;
  // #469 — equal totals pre-select nobody where a draw is not allowed: the
  // jury picks. The old 'draw' was invisible there (no Draw button), left
  // Submit enabled, and the server refused it.
  const autoWinner: '' | 'red' | 'blue' | 'draw' =
    (totals.red === 0 && totals.blue === 0) ? ''
    : totals.red > totals.blue ? 'red'
    : totals.blue > totals.red ? 'blue'
    : bout.draw_eligible === true ? 'draw' : '';
  const autoOutcome = allRoundsApproved ? 'WP' : '';
  const autoRoundEnded = highestApproved > 0 ? String(highestApproved) : '';
  const autoRedScore  = totals.red  > 0 ? String(totals.red)  : '';
  const autoBlueScore = totals.blue > 0 ? String(totals.blue) : '';

  // Auto-default on tied open-ring bout scores (IFMA v3.057 §30.8: DRAW is
  // only legal outside tournament brackets). Rendered, not stored — unlike
  // the earlier useEffect that wrote 'DRAW' into state and got stuck across
  // toggles (user unchecked R3 → auto-DRAW; rechecked R3 → scores no
  // longer tied but 'DRAW' persisted in state, masking the recomputed
  // 'WP' via the `verdictForm.outcome || autoOutcome` merge).
  // #261: key on the tournament type, never on the stage label — a
  // hand-created open-ring bout carries stage NULL and can still draw.
  // #317 — read the backend's computed gate. The hand-rolled copy that lived
  // here (added in #261) missed the dependent-bout clause and compared the
  // stage case-sensitively, so it disagreed with both the backend and
  // VerdictForm.
  const drawEligible = bout.draw_eligible === true;
  const scoresTiedAuto = totals.red > 0 && totals.red === totals.blue;
  const tiedAutoWinner = (scoresTiedAuto && drawEligible) ? 'draw' as const : '';
  const tiedAutoOutcome = (scoresTiedAuto && drawEligible) ? 'DRAW' : '';

  // Merged view — user override (non-empty field in verdictForm state) wins
  // over the tied-auto-default, which wins over the plain auto-default.
  // Storing only overrides in state means toggling R3 back cleanly
  // re-exposes the fresh auto-computed values (no stale 'DRAW' left behind).
  // #469 review — the automatic 'draw' pair on an open-ring tie belongs
  // together. Once the jury picks Red or Blue, the automatic DRAW outcome
  // yields (WP, or empty); once the jury picks a stoppage, the automatic
  // 'draw' winner yields. Before, Red + the automatic DRAW and KO + the
  // automatic 'draw' reached the server and got 400 DRAW_PAIR_INVARIANT.
  const autoWinnerShown = tiedAutoWinner || autoWinner;
  const pickedNonDrawOutcome = !!verdictForm.outcome && verdictForm.outcome !== 'DRAW';
  const shownWinner = verdictForm.winner || (pickedNonDrawOutcome && autoWinnerShown === 'draw' ? '' : autoWinnerShown);
  const displayedVerdictForm: VerdictFormState = {
    winner:            shownWinner,
    outcome:           verdictForm.outcome || (shownWinner === 'draw' ? tiedAutoOutcome : '') || autoOutcome,
    round_ended:       verdictForm.round_ended || autoRoundEnded,
    red_corner_score:  verdictForm.red_corner_score || autoRedScore,
    blue_corner_score: verdictForm.blue_corner_score || autoBlueScore,
  };

  async function handleVerdictSubmit(e: React.FormEvent) {
    e.preventDefault();
    const v = displayedVerdictForm;
    if (!v.winner || !v.outcome) return;
    // #665 — ask BEFORE the lock. A second tap in the same task is a copy of the open question: the provider answers no.
    if (verdictSend.busy) return;
    if (!(await confirm({
      message: t.bout.verdictConfirm.replace('{winner}', winnerLabel(t, v.winner)).replace('{outcome}', v.outcome),
      confirmLabel: t.bout.submit_verdict,
    }))) return;
    await verdictSend.run(async () => {
      try {
        await submitVerdict(bout.id, {
          winner: v.winner as 'red' | 'blue' | 'draw',
          outcome: v.outcome as OutcomeCode,
          round_ended: v.round_ended ? parseInt(v.round_ended) : null,
          red_corner_score:  v.red_corner_score  ? parseInt(v.red_corner_score)  : null,
          blue_corner_score: v.blue_corner_score ? parseInt(v.blue_corner_score) : null,
        });
        onVerdictSubmitted?.();
      } catch (err) {
        showError(err);
      }
    });
  }

  if (showLoading) return <div className="card p-6 text-center text-gray-400">{t.common.loading}</div>;
  if (loading) return null;

  return (
    <div className="space-y-4">

      {/* -- Jury scoring table -- jury/admin always, assigned refs after scored -- */}
      {/* #552 — a scored bout with no card, no jury total and no approved
          round (a walk-over) has nothing to show: no empty 0 / 0 table. */}
      {officialsView && bout.status !== 'pending' && (
        bout.status === 'current' || scores.length > 0 || displayRounds.length > 0
        || Object.values(overrides).some(o => o.red !== null || o.blue !== null)
      ) && (
        <JuryScoringTable
          bout={bout}
          scores={scores}
          rounds={rounds}
          displayRounds={displayRounds}
          overrides={overrides}
          savingRound={savingRound}
          hasVerdict={hasVerdict}
          canToggleDisplay={canToggleDisplay}
          onDisplayToggle={handleDisplayToggle}
          onOverride={setOverride}
          juryRounds={juryRounds}
        />
      )}

      {/* -- Public scoreboard -- everyone except assigned jury -- */}
      {!officialsView && (
        <PublicScoreboard bout={bout} publicScores={publicScores} verdict={verdict} />
      )}

      {/* -- Verdict form -- ASSIGNED JURY / ADMIN ONLY, before verdict -- */}
      {canSubmitVerdict && !hasVerdict && bout.status !== 'pending' && bout.status !== 'scored' && (
        <VerdictForm
          bout={bout}
          verdictForm={displayedVerdictForm}
          setVerdictForm={setVerdictForm}
          onSubmit={handleVerdictSubmit}
          submitting={verdictSend.busy}
          displayRounds={displayRounds}
          roundsWon={roundsWon}
        />
      )}
    </div>
  );
}
